/**
 * Ask (PRD-tasks-asks-and-my-work §4.1, FR-1). One control, drawn where the
 * thing asked about lives: an item, a cycle, a track, a session, or Home (no
 * record). It asks "What do you need?" and makes the record that answer needs:
 *
 *   An estimate or a score → a one-person session asking for one attribute on the
 *     items (lib/askForEstimate.ts). The answer lands on each item as a value
 *     proposal; the ask finishes when the person has answered every item.
 *   Something done → a request on the assignment record, living on this record
 *     (authorize_request, migration 1054), in the Requests & Agreements form.
 *   To remember it yourself → a note (create_note, migration 1058), on this record,
 *     with a reminder if wanted. Any member may write one.
 *
 * Each answer is drawn only for a person who may use it (build contract §2.8):
 * an estimate needs Facilitate Sessions (it makes a session); a request needs the
 * authority to authorize one; a note needs nothing more than being a member, so
 * a person who may do neither sees the note form straight away.
 * Nobody chooses how the other person is told: their own settings decide.
 */
import { useEffect, useMemo, useState } from 'react';
import { MessageSquarePlus, Ruler, ClipboardCheck, StickyNote } from 'lucide-react';
import { Modal, Field, Select, TextInput, TextArea, Checkbox } from '@/components/ds';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useAttributesStore } from '@/stores/prioritization/attributesStore';
import { useSystemUsers } from '@/hooks/useSystemUsers';
import { useRequestFormContext } from '@/hooks/useRequestFormContext';
import { announceRequestsChanged, holdsAssignmentAuthority, type RequestHome } from '@/lib/assignmentService';
import { askForEstimate, isEstimable } from '@/lib/askForEstimate';
import { AuthorizeAssignmentModal } from '@/components/prioritization/commitments/AssignmentsSection';
import { announceNotesChanged, createNote, type NoteSubject } from '@/lib/notesService';

export interface AskHome {
  type: Exclude<RequestHome['type'], 'commitment'>;
  /** The record; null on Home, where a request stands on its own. */
  id: string | null;
  title?: string | null;
  cycleId?: string | null;
  trackId?: string | null;
  /** The items an ask here may be about (the item itself; a session's items). */
  itemIds?: string[];
}

type Step = 'choose' | 'estimate' | 'request' | 'note';

function useMayAsk() {
  const can = useUserStore((s) => s.can);
  const mayEstimate = can('session.facilitate');
  const mayRequest = holdsAssignmentAuthority(can);
  const userId = useUserStore((s) => s.userId);
  // Anyone signed in to the workspace may write themself a note.
  return { mayEstimate, mayRequest, mayAsk: Boolean(userId) };
}

/** The Ask button: drawn only for a person who may ask for something here. */
export function AskButton({ home, label = 'Ask', className = '', style }: {
  home: AskHome;
  label?: string;
  /** The page's own button look (e.g. btnSecondary(...)); a plain outline otherwise. */
  className?: string;
  style?: React.CSSProperties;
}) {
  const { mayAsk } = useMayAsk();
  const [open, setOpen] = useState(false);
  if (!mayAsk) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={style}
        className={className || 'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--rule)] text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--hover-tint)]'}
      >
        <MessageSquarePlus className="h-3.5 w-3.5" />
        {label}
      </button>
      {open && <AskSheet home={home} onClose={() => setOpen(false)} />}
    </>
  );
}

export function AskSheet({ home, onClose, onDone }: { home: AskHome; onClose: () => void; onDone?: () => void }) {
  const { mayEstimate, mayRequest } = useMayAsk();
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const only: Step | null = !mayEstimate && !mayRequest ? 'note' : null;
  const [step, setStep] = useState<Step>(only ?? 'choose');
  const done = () => { onDone?.(); onClose(); };

  if (step === 'request') {
    return <RequestStep home={home} workspaceId={workspaceId} onClose={onClose} onDone={done} />;
  }
  if (step === 'estimate') {
    return <EstimateStep home={home} workspaceId={workspaceId} onClose={onClose} onDone={done} />;
  }
  if (step === 'note') {
    return <NoteStep home={home} workspaceId={workspaceId} onClose={onClose} onDone={done} />;
  }

  const asking = home.title ? `Asking about: ${home.title}` : 'Asking someone for something';
  return (
    <Modal open onClose={onClose} title="What do you need?" label={asking} size="md">
      <div className="space-y-2">
        {mayEstimate && (
          <ChoiceRow
            icon={<Ruler className="h-5 w-5" />}
            title="An estimate or a score"
            body="Someone gives a value (effort, value, risk, or one of your criteria) for one or more items. It lands on each item for someone who approves values to accept."
            onClick={() => setStep('estimate')}
          />
        )}
        {mayRequest && (
          <ChoiceRow
            icon={<ClipboardCheck className="h-5 w-5" />}
            title="Something done"
            body="A review, a piece of work, an answer. They accept, decline, offer something else, or say when they will know; when it is done they report it and you confirm."
            onClick={() => setStep('request')}
          />
        )}
        <ChoiceRow
          icon={<StickyNote className="h-5 w-5" />}
          title="To remember it yourself"
          body="A note only you see, on this record. Give it a date and it comes back to you in what needs you that day; mark it done when it is."
          onClick={() => setStep('note')}
        />
      </div>
    </Modal>
  );
}

function ChoiceRow({ icon, title, body, onClick }: { icon: React.ReactNode; title: string; body: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full p-3 border border-[var(--rule)] text-left flex items-start gap-3 hover:border-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
    >
      <span className="mt-0.5 text-[var(--text-ter)]">{icon}</span>
      <span>
        <span className="block font-medium text-[var(--text-pri)]">{title}</span>
        <span className="block text-sm text-[var(--text-ter)]">{body}</span>
      </span>
    </button>
  );
}

function useItemTitles(ids: string[] | undefined) {
  const items = useItemsStore((s) => s.items);
  return useMemo(
    () => (ids ?? []).map((id) => ({ id, title: items.find((i) => i.id === id)?.title ?? null })),
    [ids, items],
  );
}

function RequestStep({ home, workspaceId, onClose, onDone }: {
  home: AskHome; workspaceId: string | null; onClose: () => void; onDone: () => void;
}) {
  const ctx = useRequestFormContext(workspaceId);
  const linkedItems = useItemTitles(home.type === 'item' && home.id ? [home.id] : home.itemIds);
  const requestHome: RequestHome = { type: home.type, id: home.id, workspaceId };
  return (
    <AuthorizeAssignmentModal
      home={requestHome}
      linkedItems={linkedItems}
      users={ctx.people}
      units={ctx.units}
      workspaceId={workspaceId}
      extraGrounds={[]}
      clearRequest={ctx.requestsAndAgreements}
      delegationOn={ctx.delegationOn}
      ladder={ctx.ladder}
      amendedDelegation={null}
      onClose={onClose}
      onDone={() => {
        announceRequestsChanged(requestHome);
        window.dispatchEvent(new CustomEvent('store-success', {
          detail: { store: 'Ask', action: 'request', message: 'Asked. It is in their list until they answer.' },
        }));
        onDone();
      }}
    />
  );
}

function EstimateStep({ home, workspaceId, onClose, onDone }: {
  home: AskHome; workspaceId: string | null; onClose: () => void; onDone: () => void;
}) {
  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);
  const attributes = useAttributesStore((s) => s.attributes);
  const fetchAttributes = useAttributesStore((s) => s.fetchFromSupabase);
  useEffect(() => { if (attributes.length === 0) void fetchAttributes(); }, [attributes.length, fetchAttributes]);
  const { users } = useSystemUsers(workspaceId ? { workspaceId } : undefined);
  const allItems = useItemsStore((s) => s.items);

  const fixedItem = home.type === 'item' && home.id ? home.id : null;
  const choices = useMemo(() => {
    const pool = home.itemIds && home.itemIds.length > 0
      ? allItems.filter((i) => home.itemIds!.includes(i.id))
      : allItems;
    return pool;
  }, [allItems, home.itemIds]);

  const [attributeKey, setAttributeKey] = useState('');
  const [personId, setPersonId] = useState('');
  const [picked, setPicked] = useState<string[]>(fixedItem ? [fixedItem] : []);
  const [search, setSearch] = useState('');
  const [due, setDue] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const attribute = attributes.find((a) => a.key === attributeKey) ?? null;
  const person = users.find((u) => u.id === personId) ?? null;
  const visible = choices
    .filter((i) => !search.trim() || i.title.toLowerCase().includes(search.trim().toLowerCase()))
    .slice(0, 50);
  const ready = !!attribute && !!person && picked.length > 0 && !!workspaceId;

  async function submit() {
    if (!attribute || !person || !workspaceId) return;
    setSaving(true); setErr(null);
    const res = await askForEstimate({
      workspaceId,
      asker: { id: userId, name: userName || 'Someone' },
      person: { id: person.id, name: person.name || person.email },
      attribute,
      itemIds: picked,
      dueAt: due ? new Date(`${due}T17:00:00`) : null,
      note: note.trim() || null,
      cycleId: home.cycleId ?? (home.type === 'cycle' ? home.id : null),
      trackId: home.trackId ?? (home.type === 'track' ? home.id : null),
    });
    setSaving(false);
    if (!res.ok) { setErr(res.error); return; }
    window.dispatchEvent(new CustomEvent('store-success', {
      detail: { store: 'Ask', action: 'estimate', message: `Asked ${person.name || person.email}. You will see it waiting until they answer.` },
    }));
    onDone();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Ask for an estimate or a score"
      label={home.title ? `Asking about: ${home.title}` : undefined}
      description="They give one value per item. It lands on each item as a proposal, for someone who approves values to accept."
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 text-[var(--text-sec)] hover:text-[var(--text-pri)]">Cancel</button>
          <button
            type="button"
            disabled={!ready || saving}
            onClick={() => void submit()}
            className="text-xs px-3 py-1.5 bg-[var(--text-pri)] text-[var(--surface)] hover:opacity-90 disabled:opacity-40"
          >
            {saving ? 'Asking…' : 'Ask'}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <Field label="What value" required>
          <Select
            value={attributeKey}
            onChange={(e) => setAttributeKey(e.target.value)}
            placeholder="Effort, value, risk, a criterion…"
            options={attributes
              .filter(isEstimable)
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((a) => ({ value: a.key, label: a.label }))}
          />
        </Field>
        <Field label="Who you are asking" required>
          <Select
            value={personId}
            onChange={(e) => setPersonId(e.target.value)}
            placeholder="Choose a person"
            options={users.filter((u) => u.id !== userId).map((u) => ({ value: u.id, label: u.name || u.email }))}
          />
        </Field>
        {fixedItem ? null : (
          <Field label={`Items (${picked.length} chosen)`} required>
            <TextInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find an item" />
            <div className="mt-2 max-h-48 overflow-y-auto border border-[var(--rule)] divide-y divide-[var(--rule)]">
              {visible.length === 0 && <p className="text-xs text-[var(--text-ter)] p-2">No items match.</p>}
              {visible.map((i) => (
                <div key={i.id} className="px-2 py-1.5 text-sm hover:bg-[var(--hover-tint)]">
                  <Checkbox
                    label={i.title}
                    checked={picked.includes(i.id)}
                    onChange={(e) => setPicked((p) => (e.target.checked ? [...p, i.id] : p.filter((x) => x !== i.id)))}
                  />
                </div>
              ))}
            </div>
          </Field>
        )}
        <Field label="By when" hint="Without a date, the ask stays open two weeks.">
          <TextInput type="date" value={due} onChange={(e) => setDue(e.target.value)} />
        </Field>
        <Field label="Anything they should know">
          <TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Assume the API work is done; count only the screens." />
        </Field>
        {person && (
          <p className="text-xs text-[var(--text-ter)]">
            {person.name || person.email} is told the way they chose in their own settings, and sees it in what needs them.
          </p>
        )}
        {err && <p className="text-xs text-[var(--coral)]">{err}</p>}
      </div>
    </Modal>
  );
}

const NOTE_SUBJECTS: ReadonlySet<string> = new Set(['item', 'cycle', 'track', 'session', 'decision', 'commitment']);

function NoteStep({ home, workspaceId, onClose, onDone }: {
  home: AskHome; workspaceId: string | null; onClose: () => void; onDone: () => void;
}) {
  const [body, setBody] = useState('');
  const [day, setDay] = useState('');
  const [time, setTime] = useState('09:00');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const onRecord = home.id && NOTE_SUBJECTS.has(home.type);

  async function submit() {
    if (!workspaceId || !body.trim()) return;
    setSaving(true); setErr(null);
    const res = await createNote({
      workspaceId,
      body: body.trim(),
      subjectType: onRecord ? (home.type as NoteSubject) : null,
      subjectId: onRecord ? home.id : null,
      remindAt: day ? new Date(`${day}T${time || '09:00'}`).toISOString() : null,
    });
    setSaving(false);
    if (!res.ok) { setErr(res.error); return; }
    announceNotesChanged();
    window.dispatchEvent(new CustomEvent('store-success', {
      detail: { store: 'Ask', action: 'note', message: day ? 'Noted. It comes back to you on the day.' : 'Noted. It is on My work under Notes.' },
    }));
    onDone();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="A note to yourself"
      label={home.title ? `On: ${home.title}` : undefined}
      description="Only you see it. With a date, it comes back to you in what needs you; without one, it waits on My work."
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 text-[var(--text-sec)] hover:text-[var(--text-pri)]">Cancel</button>
          <button
            type="button"
            disabled={!body.trim() || saving || !workspaceId}
            onClick={() => void submit()}
            className="text-xs px-3 py-1.5 bg-[var(--text-pri)] text-[var(--surface)] hover:opacity-90 disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Keep it'}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <Field label="What to remember" required>
          <TextArea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Ask Dana whether the two migration items count as one." />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Remind me on" hint="Leave empty for no reminder.">
            <TextInput type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          </Field>
          <Field label="At">
            <TextInput type="time" value={time} onChange={(e) => setTime(e.target.value)} disabled={!day} />
          </Field>
        </div>
        {err && <p className="text-xs text-[var(--coral)]">{err}</p>}
      </div>
    </Modal>
  );
}
